import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { GraduationCap, Check, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';

const AVATAR_LABELS = {
  purple: 'Purple', blue: 'Blue', teal: 'Teal', green: 'Green', orange: 'Orange', red: 'Red',
};

export default function AdminCoachSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [avatar, setAvatar] = useState('purple');
  const [coachName, setCoachName] = useState('');
  const [defaultName, setDefaultName] = useState('Brandy');
  const [avatars, setAvatars] = useState(['purple', 'blue', 'teal', 'green', 'orange', 'red']);

  useEffect(() => {
    api.getCoachAdminSettings()
      .then((r) => {
        setEnabled(!!r.enabled);
        setAvatar(r.avatar || 'purple');
        setCoachName(r.name || '');
        if (r.default_name) setDefaultName(r.default_name);
        if (Array.isArray(r.avatars) && r.avatars.length) setAvatars(r.avatars);
      })
      .catch((e) => toast.error(e.message || 'Could not load Coach settings'))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.updateCoachAdminSettings({ enabled, avatar, coach_name: coachName.trim() });
      setEnabled(!!r.enabled);
      setAvatar(r.avatar || avatar);
      setCoachName(r.name || '');
      toast.success('Coach settings saved');
    } catch (e) {
      toast.error(e.message || 'Could not save Coach settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground" data-testid="coach-settings-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-8 space-y-6" data-testid="admin-coach-settings">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-primary/15 flex items-center justify-center">
          <GraduationCap className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-foreground">AI Coach</h1>
          <p className="text-sm text-muted-foreground">Turn the in-app Coach on or off and choose its branding avatar.</p>
        </div>
      </div>

      {/* Enable toggle */}
      <Card className="p-5 flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-foreground">Enable AI Coach</p>
          <p className="text-xs text-muted-foreground mt-0.5">Shows the guided-walkthrough Coach for everyone in this workspace.</p>
        </div>
        <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="coach-enabled-switch" aria-label="Enable AI Coach" />
      </Card>

      {/* Coach name */}
      <Card className="p-5 space-y-3">
        <div>
          <p className="text-sm font-medium text-foreground">Coach name</p>
          <p className="text-xs text-muted-foreground mt-0.5">The name shown in the Coach popup and its tagline. Leave blank to use “{defaultName}”.</p>
        </div>
        <input
          type="text"
          value={coachName}
          onChange={(e) => setCoachName(e.target.value)}
          maxLength={40}
          placeholder={defaultName}
          data-testid="coach-name-input"
          className="w-full text-sm px-3 py-2 rounded-lg border border-border/60 bg-background focus:outline-none focus:ring-2 focus:ring-primary/40"
        />
        <p className="text-[11px] text-muted-foreground">Preview: <span className="text-foreground font-medium">{(coachName.trim() || defaultName)}</span> — “Let {(coachName.trim() || defaultName)} show you how”</p>
      </Card>

      {/* Avatar picker */}
      <Card className="p-5 space-y-4">
        <div>
          <p className="text-sm font-medium text-foreground">Coach avatar</p>
          <p className="text-xs text-muted-foreground mt-0.5">Pick the robot that represents the Coach across the app.</p>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
          {avatars.map((key) => {
            const selected = key === avatar;
            return (
              <button
                key={key}
                type="button"
                data-testid={`coach-avatar-${key}`}
                aria-pressed={selected}
                onClick={() => setAvatar(key)}
                className={`relative flex flex-col items-center gap-1.5 rounded-xl border p-2.5 transition-all focus:outline-none focus:ring-2 focus:ring-primary/40 ${selected ? 'border-primary bg-primary/10 ring-1 ring-primary' : 'border-border/50 hover:border-border hover:bg-muted/30'}`}
              >
                {selected && (
                  <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
                    <Check className="w-3 h-3" />
                  </span>
                )}
                <img src={`/coach_avatars/coach_${key}.png`} alt={AVATAR_LABELS[key] || key} className="w-14 h-14 object-contain" />
                <span className="text-[11px] text-muted-foreground">{AVATAR_LABELS[key] || key}</span>
              </button>
            );
          })}
        </div>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="coach-settings-save" className="gap-2">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          Save changes
        </Button>
      </div>
    </div>
  );
}
